import React from "react";

export function DotLeaderRow({ label, value, bold = false }) {
  return (
    <div className="ic-dot-leader-row" data-testid={`dot-leader-${label?.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="ic-dot-leader-label">{label}</span>
      <span className="ic-dot-leader-dots" />
      <span className="ic-dot-leader-value" style={bold ? { fontWeight: 700 } : undefined}>{value}</span>
    </div>
  );
}

export function SectionHeader({ title, icon }) {
  return (
    <div className="ic-section-header" data-testid={`section-header-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      {icon && <span className="ic-section-header-icon">{icon}</span>}
      <span>{title}</span>
    </div>
  );
}

export function MetricPill({ label, value, highlight = false }) {
  return (
    <div className={`ic-metric-pill${highlight ? " highlight" : ""}`} data-testid={`metric-pill-${label?.toLowerCase()}`}>
      <span className="ic-metric-pill-label">{label}</span>
      <span className="ic-metric-pill-value">{value}</span>
    </div>
  );
}

const STATUS_MAP = {
  open: { color: "var(--ic-status-open)", bg: "rgba(64,145,108,0.1)" },
  active: { color: "var(--ic-status-open)", bg: "rgba(64,145,108,0.1)" },
  approved: { color: "var(--ic-status-open)", bg: "rgba(64,145,108,0.1)" },
  confirmed: { color: "var(--ic-status-funded)", bg: "rgba(27,67,50,0.1)" },
  funded: { color: "var(--ic-status-funded)", bg: "rgba(27,67,50,0.1)" },
  pending: { color: "var(--ic-status-pending)", bg: "rgba(217,119,6,0.1)" },
  review: { color: "var(--ic-status-pending)", bg: "rgba(217,119,6,0.1)" },
  reviewing: { color: "var(--ic-status-pending)", bg: "rgba(217,119,6,0.1)" },
  submitted: { color: "var(--ic-status-pending)", bg: "rgba(217,119,6,0.1)" },
  withdrawn: { color: "var(--ic-status-withdrawn)", bg: "rgba(156,163,175,0.1)" },
  passed: { color: "var(--ic-status-withdrawn)", bg: "rgba(156,163,175,0.1)" },
  rejected: { color: "var(--ic-status-rejected)", bg: "rgba(220,38,38,0.1)" },
  declined: { color: "var(--ic-status-rejected)", bg: "rgba(220,38,38,0.1)" },
};

export function StatusBadge({ status }) {
  const key = (status || "").toLowerCase().replace(/[^a-z]/g, "");
  const style = STATUS_MAP[key] || STATUS_MAP.pending;
  return (
    <span
      className="ic-status-badge"
      style={{ color: style.color, background: style.bg }}
      data-testid={`status-badge-${key}`}
    >
      {status}
    </span>
  );
}

export function ScoreBadge({ score }) {
  let bg, color;
  if (score >= 70) { bg = "#DCFCE7"; color = "#14532D"; }
  else if (score >= 50) { bg = "#D1FAE5"; color = "#065F46"; }
  else { bg = "#FEF3C7"; color = "#92400E"; }

  return (
    <span
      className="ic-score-badge"
      style={{ background: bg, color }}
      title="Imaani Underwriting Score (0-100). Based on LTV, LTC, borrower experience, market conditions, and deal structure."
      data-testid="score-badge"
    >
      Score: {score}
    </span>
  );
}

export function MapThumbnail({ address, height = 140 }) {
  const [failed, setFailed] = React.useState(false);
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const encoded = encodeURIComponent(address || "");

  const mapUrl = key && address
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${encoded}&zoom=14&size=400x${height}&maptype=roadmap&markers=color:0x1B4332|${encoded}&style=feature:all|element:labels.text.fill|color:0x1B4332&key=${key}`
    : null;

  if (!mapUrl || failed) {
    return (
      <div
        className="ic-map-fallback"
        style={{ height }}
        data-testid="map-thumbnail-fallback"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span className="ic-map-fallback-text">{address || "Location pending"}</span>
      </div>
    );
  }

  return (
    <img
      src={mapUrl}
      alt={address}
      className="ic-map-thumbnail"
      style={{ height }}
      onError={() => setFailed(true)}
      data-testid="map-thumbnail"
    />
  );
}

export function AvatarBadge({ name, size = 48 }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      className="ic-avatar-badge"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      data-testid="avatar-badge"
    >
      {initials}
    </div>
  );
}

export function DotLeaderTable({ columns, rows, emptyText }) {
  if (!rows || rows.length === 0) {
    return emptyText ? (
      <div className="ic-table-empty" data-testid="dot-leader-table-empty">{emptyText}</div>
    ) : null;
  }

  return (
    <div className="ic-table-scroll" data-testid="dot-leader-table">
      <table className="ic-dot-leader-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i}>
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
